'use client';

import { type ComponentRef, useEffect, useMemo, useRef, useState } from 'react';
import { useEvent, useMount } from 'react-use';

const useHeaderHeight = () => {
    const [headerHeight, setHeaderHeight] = useState('3.5rem');
    const [fontSize, setFontSize] = useState('16px');

    useMount(() => {
        setHeaderHeight(getComputedStyle(document.body).getPropertyValue('--header-height'));
        setFontSize(getComputedStyle(document.documentElement).fontSize);
    });

    useEvent('resize', () => {
        setHeaderHeight(getComputedStyle(document.body).getPropertyValue('--header-height'));
        setFontSize(getComputedStyle(document.documentElement).fontSize);
    });

    return useMemo(() => {
        if (headerHeight.endsWith('rem')) {
            return Number.parseFloat(headerHeight) * Number.parseFloat(fontSize);
        }

        return Number.parseFloat(headerHeight);
    }, [headerHeight, fontSize]);
};

export const useSubcategoryRef = () => useRef<ComponentRef<'div'>>(null);

type UseHeaderOffsetOpts = {
    subcategoryRef: ReturnType<typeof useSubcategoryRef>;
};

export const useHeaderOffset = ({ subcategoryRef }: UseHeaderOffsetOpts) => {
    const [mounted, setMounted] = useState(false);

    const headerHeight = useHeaderHeight();
    const intersectionHeight = headerHeight - (subcategoryRef.current?.offsetHeight ?? 0);

    const [headerOffset, setHeaderOffset] = useState(0);

    useEffect(() => {
        if (!mounted) {
            setMounted(true);
            return;
        }

        let previousScrollY = window.scrollY;

        const onScrollListener = () => {
            const delta = window.scrollY - previousScrollY;

            setHeaderOffset(prev => Math.min(Math.max(prev - delta, intersectionHeight), headerHeight));

            previousScrollY = window.scrollY;
        };

        window.addEventListener('scroll', onScrollListener);

        return () => window.removeEventListener('scroll', onScrollListener);
    }, [mounted, headerHeight, intersectionHeight]);

    return headerOffset;
};
